import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import PersonaRoutine, { Moment } from '@comp/persona/persona-routine'
import PersonaHelpLinks, { HelpLinkGroup } from '@comp/persona/persona-help-links'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  routineEyebrow: string
  routineTitle: string
  routineBody: string
  moments: Moment[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  helpEyebrow: string
  helpTitle: string
  helpBody: string
  helpGroups: HelpLinkGroup[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Monofor for IT admins',
    metaDescription:
      'Provisioning, password reset, directory sync, dashboards, and audits: Monofor handles the operational load so your team stops firefighting and starts running an identity program.',
    heroEyebrow: 'For IT admins',
    heroTitle: ['Run identity ops', 'like a service.'],
    heroBody:
      'Provisioning, password reset, directory sync, dashboards, and audits: all on one console. Less tickets, more sleep.',
    badges: ['HRIS-driven JML', 'AD / LDAP / OUD / WebAPI', 'Live ops dashboards'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    routineEyebrow: 'A day with Monofor',
    routineTitle: 'A day in the life of an IT admin on Monofor.',
    routineBody:
      'No more spreadsheet-driven provisioning, no more 3am password tickets, no more guessing which app forgot to deprovision.',
    moments: [
      {
        time: '08:30',
        title: 'New-hire batch lands',
        body: 'Workday fires fifteen joiner events. Monofor pre-provisioned email, SSO, and MFA enrollment links: no tickets, no spreadsheets, no missed accesses.',
      },
      {
        time: '11:15',
        title: 'Manager-driven access request',
        body: 'A manager requests Jira admin access for a senior engineer. The approval flow runs through Slack; access is granted, scoped, and logged before lunch.',
      },
      {
        time: '16:45',
        title: 'End-of-day offboarding',
        body: 'HR records two departures. Monofor disables identity in AD, revokes app access through Monosync connectors, and exports an evidence packet for HR records, done before you log off.',
      },
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What IT admins actually get.',
    pillarsBody:
      'Four operational capabilities that replace the tickets, spreadsheets, and side-channels most IT teams still run on.',
    pillars: [
      {
        title: 'Provision and deprovision without tickets',
        description:
          'Joiner / mover / leaver workflows fire on HRIS events. New hires get the right bundle on day one; leavers lose access the same hour.',
        bullets: [
          'HRIS-driven JML automation',
          'Role-based access bundles',
          'Self-service access requests',
          'Same-day offboarding evidence',
        ],
      },
      {
        title: 'One console, every directory',
        description:
          'AD, OpenLDAP, Oracle Unified Directory, WebAPI sources: Monosign normalizes them so policy applies everywhere consistently.',
        bullets: [
          'Active Directory and Entra ID',
          'OpenLDAP and OUD profile sync',
          'WebAPI source for in-house systems',
          'Live delta sync, observable per source',
        ],
      },
      {
        title: 'Password resets that do not page you',
        description:
          'Self-service password reset with identity proofing, plus help-desk-assisted recovery for the edge cases.',
        bullets: [
          'Self-service reset with MFA challenge',
          'Help-desk-assisted recovery flow',
          'Privileged user lockout policy',
          'Audit trail per reset event',
        ],
      },
      {
        title: 'Dashboards built for an ops shift',
        description:
          'Active sessions, recent audits, sync health, and provisioning queues: all on one screen, not a 7-tab BI export.',
        bullets: [
          'Live active-session view',
          'Per-source sync health metrics',
          'Failed-authentication anomalies',
          'Exportable audit reports',
        ],
      },
    ],
    helpEyebrow: 'Straight to the docs',
    helpTitle: 'Docs your team will actually open.',
    helpBody:
      'Direct deep-links into the help center, organized the way your week is.',
    helpGroups: [
      {
        title: 'Lifecycle & users',
        links: [
          { label: 'New user', description: 'Provision identities into Monosign', href: 'https://help.monofor.com/monosign/new-user' },
          { label: 'Add user from source', description: 'Pull users from directories or HRIS', href: 'https://help.monofor.com/monosign/add-user-from-source' },
          { label: 'Reset user password', description: 'Self-service and help-desk-assisted', href: 'https://help.monofor.com/monosign/reset-users-password' },
          { label: 'Access packages', description: 'Bundle entitlements by role', href: 'https://help.monofor.com/monosign/access-packages' },
        ],
      },
      {
        title: 'Directory sync',
        links: [
          { label: 'Active Directory source', description: 'Read and sync AD users and profiles', href: 'https://help.monofor.com/monosign/configuration-directory-sources-ad' },
          { label: 'LDAP source', description: 'Generic LDAP v3 directories', href: 'https://help.monofor.com/monosign/configuration-directory-sources-ldap' },
          { label: 'Oracle Unified Directory', description: 'OUD user and profile sync', href: 'https://help.monofor.com/monosign/oracle-unified-directory-oud-user-and-profile-sync' },
          { label: 'WebAPI source', description: 'In-house systems as identity sources', href: 'https://help.monofor.com/monosign/configuration-directory-sources-webapi' },
        ],
      },
      {
        title: 'Audits & dashboards',
        links: [
          { label: 'Dashboard overview', description: 'Live operations view', href: 'https://help.monofor.com/monosign/dashboard' },
          { label: 'Active sessions', description: 'Monitor and revoke live sessions', href: 'https://help.monofor.com/monosign/active-sessions' },
          { label: 'Subscribe to audits', description: 'Stream audit events out', href: 'https://help.monofor.com/monosign/subscribe-to-audits' },
          { label: 'Reports', description: 'Generate and export reports', href: 'https://help.monofor.com/monosign/reports' },
        ],
      },
      {
        title: 'Troubleshooting',
        links: [
          { label: 'Troubleshooting overview', href: 'https://help.monofor.com/monosign/troubleshooting' },
          { label: 'AdminSDHolder permission issue', href: 'https://help.monofor.com/monosign/adminsdholder-user-account-permission-issue' },
          { label: 'How to unlock an account', href: 'https://help.monofor.com/monosign/how-to-unlock-account-recover-password' },
          { label: 'Monosign FAQ', href: 'https://help.monofor.com/monosign/faq' },
        ],
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'For Security Engineers', description: 'MFA, PAM, audit, anomaly detection.', href: '/for/security-engineers' },
      { label: 'For Developers', description: 'REST API, OIDC, SAML, JWT, SDKs.', href: '/for/developers' },
      { label: 'For Compliance Officers', description: 'Audit, attestation, framework reports.', href: '/for/compliance-officers' },
    ],
  },
  tr: {
    metaTitle: 'BT yöneticileri için Monofor',
    metaDescription:
      'Kullanıcı sağlama, parola sıfırlama, dizin senkronizasyonu, panolar ve denetimler: operasyonel yükü Monofor üstlenir; ekibiniz yangın söndürmeyi bırakıp bir kimlik programı yürütmeye başlar.',
    heroEyebrow: 'BT yöneticileri için',
    heroTitle: ['Kimlik operasyonlarını', 'bir servis gibi yönetin.'],
    heroBody:
      'Kullanıcı sağlama, parola sıfırlama, dizin senkronizasyonu, panolar ve denetimler: hepsi tek konsolda. Daha az talep kaydı, daha çok uyku.',
    badges: ['HRIS güdümlü JML', 'AD / LDAP / OUD / WebAPI', 'Canlı operasyon panoları'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    routineEyebrow: 'Monofor ile bir gün',
    routineTitle: 'Monofor kullanan bir BT yöneticisinin bir günü.',
    routineBody:
      'Artık tablolarla yürüyen kullanıcı sağlama yok, gece 3 parola talepleri yok, hangi uygulamanın erişimi kaldırmayı unuttuğunu tahmin etmek yok.',
    moments: [
      {
        time: '08:30',
        title: 'Yeni işe alım grubu geliyor',
        body: 'Workday on beş işe giriş olayı gönderiyor. Monofor e-postayı, SSO’yu ve MFA kayıt bağlantılarını önceden sağlamış: talep kaydı yok, tablo yok, atlanan erişim yok.',
      },
      {
        time: '11:15',
        title: 'Yönetici kaynaklı erişim talebi',
        body: 'Bir yönetici, kıdemli bir mühendis için Jira admin erişimi talep ediyor. Onay akışı Slack üzerinden yürüyor; erişim öğleden önce verilmiş, kapsamı belirlenmiş ve loglanmış oluyor.',
      },
      {
        time: '16:45',
        title: 'Gün sonu işten çıkışı',
        body: 'İK iki ayrılış kaydediyor. Monofor kimliği AD’de devre dışı bırakıyor, Monosync bağlayıcılarıyla uygulama erişimlerini iptal ediyor ve İK kayıtları için bir kanıt paketi dışa aktarıyor; siz çıkış yapmadan tamamlanıyor.',
      },
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'BT yöneticilerinin gerçekten elde ettikleri.',
    pillarsBody:
      'Çoğu BT ekibinin hâlâ dayandığı talep kayıtlarının, tabloların ve yan kanalların yerini alan dört operasyonel yetenek.',
    pillars: [
      {
        title: 'Talep kaydı olmadan sağlama ve kaldırma',
        description:
          'İşe giriş / geçiş / ayrılış akışları HRIS olaylarıyla tetiklenir. Yeni çalışanlar ilk gün doğru pakete kavuşur; ayrılanlar aynı saat içinde erişimini kaybeder.',
        bullets: [
          'HRIS güdümlü JML otomasyonu',
          'Rol tabanlı erişim paketleri',
          'Self servis erişim talepleri',
          'Aynı gün işten çıkış kanıtı',
        ],
      },
      {
        title: 'Tek konsol, tüm dizinler',
        description:
          'AD, OpenLDAP, Oracle Unified Directory, WebAPI kaynakları: Monosign hepsini normalize eder; politika her yerde tutarlı uygulanır.',
        bullets: [
          'Active Directory ve Entra ID',
          'OpenLDAP ve OUD profil senkronizasyonu',
          'Kurum içi sistemler için WebAPI kaynağı',
          'Kaynak bazında izlenebilir canlı delta senkronizasyonu',
        ],
      },
      {
        title: 'Sizi gece uyandırmayan parola sıfırlama',
        description:
          'Kimlik doğrulamalı self servis parola sıfırlama; uç durumlar için yardım masası destekli kurtarma.',
        bullets: [
          'MFA doğrulamalı self servis sıfırlama',
          'Yardım masası destekli kurtarma akışı',
          'Ayrıcalıklı kullanıcı kilitleme politikası',
          'Her sıfırlama olayı için denetim izi',
        ],
      },
      {
        title: 'Operasyon vardiyası için tasarlanmış panolar',
        description:
          'Aktif oturumlar, son denetimler, senkronizasyon sağlığı ve sağlama kuyrukları: 7 sekmeli bir BI dışa aktarımı değil, tek ekran.',
        bullets: [
          'Canlı aktif oturum görünümü',
          'Kaynak bazında senkronizasyon sağlığı metrikleri',
          'Başarısız kimlik doğrulama anomalileri',
          'Dışa aktarılabilir denetim raporları',
        ],
      },
    ],
    helpEyebrow: 'Doğrudan dokümantasyona',
    helpTitle: 'Ekibinizin gerçekten açacağı dokümanlar.',
    helpBody:
      'Yardım merkezine doğrudan derin bağlantılar, haftanızın akışına göre düzenlenmiş.',
    helpGroups: [
      {
        title: 'Yaşam döngüsü ve kullanıcılar',
        links: [
          { label: 'Yeni kullanıcı', description: 'Monosign’a kimlik sağlayın', href: 'https://help.monofor.com/monosign/new-user' },
          { label: 'Kaynaktan kullanıcı ekleme', description: 'Dizinlerden veya HRIS’ten kullanıcı çekin', href: 'https://help.monofor.com/monosign/add-user-from-source' },
          { label: 'Kullanıcı parolası sıfırlama', description: 'Self servis ve yardım masası destekli', href: 'https://help.monofor.com/monosign/reset-users-password' },
          { label: 'Erişim paketleri', description: 'Yetkileri role göre paketleyin', href: 'https://help.monofor.com/monosign/access-packages' },
        ],
      },
      {
        title: 'Dizin senkronizasyonu',
        links: [
          { label: 'Active Directory kaynağı', description: 'AD kullanıcılarını ve profillerini okuyun ve senkronize edin', href: 'https://help.monofor.com/monosign/configuration-directory-sources-ad' },
          { label: 'LDAP kaynağı', description: 'Genel LDAP v3 dizinleri', href: 'https://help.monofor.com/monosign/configuration-directory-sources-ldap' },
          { label: 'Oracle Unified Directory', description: 'OUD kullanıcı ve profil senkronizasyonu', href: 'https://help.monofor.com/monosign/oracle-unified-directory-oud-user-and-profile-sync' },
          { label: 'WebAPI kaynağı', description: 'Kimlik kaynağı olarak kurum içi sistemler', href: 'https://help.monofor.com/monosign/configuration-directory-sources-webapi' },
        ],
      },
      {
        title: 'Denetimler ve panolar',
        links: [
          { label: 'Panoya genel bakış', description: 'Canlı operasyon görünümü', href: 'https://help.monofor.com/monosign/dashboard' },
          { label: 'Aktif oturumlar', description: 'Canlı oturumları izleyin ve sonlandırın', href: 'https://help.monofor.com/monosign/active-sessions' },
          { label: 'Denetimlere abone olma', description: 'Denetim olaylarını dışarı aktarın', href: 'https://help.monofor.com/monosign/subscribe-to-audits' },
          { label: 'Raporlar', description: 'Rapor oluşturun ve dışa aktarın', href: 'https://help.monofor.com/monosign/reports' },
        ],
      },
      {
        title: 'Sorun giderme',
        links: [
          { label: 'Sorun gidermeye genel bakış', href: 'https://help.monofor.com/monosign/troubleshooting' },
          { label: 'AdminSDHolder izin sorunu', href: 'https://help.monofor.com/monosign/adminsdholder-user-account-permission-issue' },
          { label: 'Hesap kilidini açma', href: 'https://help.monofor.com/monosign/how-to-unlock-account-recover-password' },
          { label: 'Monosign SSS', href: 'https://help.monofor.com/monosign/faq' },
        ],
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Güvenlik mühendisleri için', description: 'MFA, PAM, denetim, anomali tespiti.', href: '/for/security-engineers' },
      { label: 'Geliştiriciler için', description: 'REST API, OIDC, SAML, JWT, SDK’lar.', href: '/for/developers' },
      { label: 'Uyum sorumluları için', description: 'Denetim, erişim onayları, çerçeve raporları.', href: '/for/compliance-officers' },
    ],
  },
}

type Props = { locale: string }

export default function ItAdminsPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/for/it-admins"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <PersonaRoutine
          eyebrow={c.routineEyebrow}
          title={c.routineTitle}
          body={c.routineBody}
          moments={c.moments}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={c.pillarsTitle}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <PersonaHelpLinks
          eyebrow={c.helpEyebrow}
          title={c.helpTitle}
          body={c.helpBody}
          groups={c.helpGroups}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
